import { BackSide, Color, Euler, FrontSide } from "three";

import { computePosition } from "../utils/position";
import { createCanvasTexture } from "./renderUtils";

import { adjustDimensions, meterToCanvas } from "./zoomHelper";

export type ElementTypeRenderProps = {
	onSelectElement: (elementId: string) => void;
	active?: boolean;
};

const Element: React.FC<
	{ element: ElementDefinitionType; position: ElementPositionType } & ElementTypeRenderProps
> = ({ element, position: eltPos, onSelectElement, active }) => {
	[element, eltPos] = adjustDimensions(element, eltPos, meterToCanvas);
	const { id, color } = element;
	const { position = [0, 0, 0], rotX = 0, rotY = 0, rotZ = 0 } = eltPos;

	const parts = element.getParts();
	if (parts.length === 0) {
		console.error("No parts to render for", element);
		return null;
	}
	const colorComponent = new Color(color);
	const correctedPosition = computePosition(element, {
		position,
		rotX,
		rotY,
		rotZ
	});

	const materialText = (
		<meshStandardMaterial
			attachArray="material"
			map={createCanvasTexture(id, colorComponent)}
			transparent
			opacity={active ? 0.5 : 1}
			side={FrontSide}
		/>
	);
	const materialNoText = (
		<meshStandardMaterial
			attachArray="material"
			color={colorComponent}
			transparent
			opacity={active ? 0.5 : 1}
			side={FrontSide}
		/>
	);
	const materialBack = (
		<meshStandardMaterial
			color={colorComponent}
			transparent
			opacity={active ? 0.8 : 1}
			side={BackSide}
		/>
	);
	const rotation = new Euler((rotX * Math.PI) / 2, (rotY * Math.PI) / 2, (rotZ * Math.PI) / 2);

	const textPart = parts.shift() as PartDefinition; // Force type to NOT undefined, as we checked the length earlier...
	const geometryFirst = <boxBufferGeometry args={textPart.dimensions as Vector} />;
	// Transparency managed based on https://threejsfundamentals.org/threejs/lessons/threejs-transparency.html
	// Needed to see all sides with transparency
	return (
		<group
			position={correctedPosition}
			rotation={rotation}
			// Commented for now, crashes the app
			// Probably a render issue, the elements (material) are removed where they are still in use by threejs
			// See https://github.com/pmndrs/react-three-fiber/blob/master/markdown/pitfalls.md#do-not-mount-unmount-indiscriminately ?
			// onClick={event => {
			// 	onSelectElement(id);
			// 	event.stopPropagation();
			// }}
		>
			<group position={textPart.position}>
				<mesh>
					{geometryFirst}
					{materialBack}
				</mesh>
				<mesh>
					{geometryFirst}
					{materialText}
					{materialText}
					{materialNoText}
					{materialNoText}
					{materialText}
					{materialText}
				</mesh>
			</group>
			{parts.map((part, index) => {
				// Geometry for the top of the table... Needs to be in a group that will be translated...
				const geometry = <boxBufferGeometry args={part.dimensions as Vector} />;
				return (
					<group key={index} position={part.position}>
						<mesh>
							{geometry}
							{materialBack}
						</mesh>
						<mesh>
							{geometry}
							{materialNoText}
						</mesh>
					</group>
				);
			})}
		</group>
	);
};

export default Element;
